/**
 * FILE: apps/hub/src/router.tsx
 * PURPOSE: All application routes with React.lazy code-splitting for heavy pages.
 * AGENT_CONTEXT: CRITICAL — Add new routes here. Lazy-load pages >200 lines.
 * INVARIANTS: Use React.lazy for heavy routes. Wrap in Suspense with fallback.
 * LAST_REVIEWED: 2026-05-18
 */

/* eslint-disable react-refresh/only-export-components --
   Lazy route modules are intentionally co-located; file also exports router config (non-component). */
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary'
import { EnrollPage } from '@/pages/enroll'
import { LoginPage } from '@/pages/Login'
import { NotFoundPage } from '@/pages/NotFound'
import { SettingsPage } from '@/pages/settings'
import { WebsiteSyncSettingsPage } from '@/pages/settings/WebsiteSyncSettingsPage'
import { CourseAttemptsSettingsPage } from '@/pages/settings/CourseAttemptsSettingsPage'
import { SupportLayoutPage } from '@/pages/support/Support'
import { LegacyEditRedirect } from '@/LegacyEditRedirect'

const ActivityPage = lazy(() =>
  import('@/pages/Activity').then((m) => ({ default: m.ActivityPage })),
)
const EditStudentPage = lazy(() =>
  import('@/pages/EditStudent').then((m) => ({ default: m.EditStudentPage })),
)
const NotificationsPage = lazy(() =>
  import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
)
const WebsiteSyncConsolePage = lazy(() =>
  import('@/pages/admin/WebsiteSyncConsolePage').then((m) => ({ default: m.WebsiteSyncConsolePage })),
)
const StudentsPage = lazy(() =>
  import('@/pages/students').then((m) => ({ default: m.StudentsPage })),
)
const StudentsLayoutPage = lazy(() =>
  import('@/pages/students/StudentsLayoutPage').then((m) => ({ default: m.StudentsLayoutPage })),
)
const AddExistingStudentPage = lazy(() =>
  import('@/pages/students/AddExistingStudentPage').then((m) => ({ default: m.AddExistingStudentPage })),
)
const DuplicateStudentsPage = lazy(() =>
  import('@/pages/students/DuplicateStudentsPage').then((m) => ({ default: m.DuplicateStudentsPage })),
)
const PlacementsAddExistingStudentPage = lazy(() =>
  import('@/pages/placements/AddExistingStudentPage').then((m) => ({ default: m.AddExistingStudentPage })),
)
const CommunityLinksPage = lazy(() =>
  import('@/pages/support/CommunityLinksPage').then((m) => ({ default: m.CommunityLinksPage })),
)
const PlacementsCommunityLinksPage = lazy(() =>
  import('@/pages/placements/PlacementsCommunityLinksPage').then((m) => ({ default: m.PlacementsCommunityLinksPage })),
)

const suspenseFallback = (
  <div className="flex h-screen items-center justify-center">
    <LoadingSpinner />
  </div>
)

const StudentProfilePage = lazy(() =>
  import('@/pages/StudentProfile').then((m) => ({ default: m.StudentProfilePage })),
)
const PlacementsLayoutPage = lazy(() =>
  import('@/pages/placements/PlacementsLayout').then((m) => ({
    default: m.PlacementsLayoutPage,
  })),
)
const PlacementsStudentProfilePage = lazy(() =>
  import('@/pages/placements/PlacementsStudentProfilePage').then((m) => ({
    default: m.PlacementsStudentProfilePage,
  })),
)
const JobsPage = lazy(() =>
  import('@/pages/placements/Jobs').then((m) => ({ default: m.JobsPage })),
)
const JobDetailPage = lazy(() =>
  import('@/pages/placements/JobDetail').then((m) => ({ default: m.JobDetailPage })),
)
const CandidatesPage = lazy(() =>
  import('@/pages/placements/Candidates').then((m) => ({ default: m.CandidatesPage })),
)
const ApplicationsPage = lazy(() =>
  import('@/pages/placements/Applications').then((m) => ({ default: m.ApplicationsPage })),
)
const TrackerPage = lazy(() =>
  import('@/pages/placements/Tracker').then((m) => ({ default: m.TrackerPage })),
)
const CompaniesPage = lazy(() =>
  import('@/pages/placements/Companies').then((m) => ({ default: m.CompaniesPage })),
)
const RecruitersPage = lazy(() =>
  import('@/pages/placements/Recruiters').then((m) => ({ default: m.RecruitersPage })),
)
const PlacementsInboxPage = lazy(() =>
  import('@/pages/placements/PlacementsInboxPage').then((m) => ({
    default: m.PlacementsInboxPage,
  })),
)
const PlacementHistoryPage = lazy(() =>
  import('@/pages/placements/PlacementHistoryPage').then((m) => ({ default: m.PlacementHistoryPage })),
)
const OutreachFollowupsPage = lazy(() =>
  import('@/pages/support/outreach/OutreachFollowupsPage').then((m) => ({
    default: m.OutreachFollowupsPage,
  })),
)
const ChatbotLayoutPage = lazy(() =>
  import('@/pages/support/chatbot/ChatbotLayoutPage').then((m) => ({ default: m.ChatbotLayoutPage })),
)
const ChatbotInboxPage = lazy(() =>
  import('@/pages/support/chatbot/ChatbotInboxPage').then((m) => ({ default: m.ChatbotInboxPage })),
)
const ChatbotTicketDetailPage = lazy(() =>
  import('@/pages/support/chatbot/ChatbotTicketDetailPage').then((m) => ({ default: m.ChatbotTicketDetailPage })),
)
const ChatbotKbPage = lazy(() =>
  import('@/pages/support/chatbot/ChatbotKbPage').then((m) => ({ default: m.ChatbotKbPage })),
)
const ChatbotMissedPage = lazy(() =>
  import('@/pages/support/chatbot/ChatbotMissedPage').then((m) => ({ default: m.ChatbotMissedPage })),
)
const ChatbotAnalyticsPage = lazy(() =>
  import('@/pages/support/chatbot/ChatbotAnalyticsPage').then((m) => ({ default: m.ChatbotAnalyticsPage })),
)
const DocsPage = lazy(() =>
  import('@/pages/DocsPage').then((m) => ({ default: m.DocsPage })),
)

const AppShell = lazy(() => import('@/layouts/AppShell').then((m) => ({ default: m.AppShell })))
const AssistantHomePage = lazy(() =>
  import('@/pages/AssistantHomePage').then((m) => ({ default: m.AssistantHomePage })),
)
const DashboardPage = lazy(() =>
  import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })),
)
const McpApprovePage = lazy(() =>
  import('@/pages/McpApprovePage').then((m) => ({ default: m.McpApprovePage })),
)
const AnalyticsLayoutPage = lazy(() =>
  import('@/pages/analytics/AnalyticsLayout').then((m) => ({ default: m.AnalyticsLayoutPage })),
)
const AnalyticsOverviewPage = lazy(() =>
  import('@/pages/analytics/OverviewPage').then((m) => ({ default: m.AnalyticsOverviewPage })),
)
const PlacementsAnalyticsPage = lazy(() =>
  import('@/pages/analytics/PlacementsAnalyticsPage').then((m) => ({ default: m.PlacementsAnalyticsPage })),
)

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/mcp/approve',
        element: (
          <Suspense fallback={suspenseFallback}>
            <McpApprovePage />
          </Suspense>
        ),
      },
      {
        errorElement: <ChunkErrorBoundary />,
        element: (
          <Suspense fallback={suspenseFallback}>
            <AppShell />
          </Suspense>
        ),
        children: [
          {
            element: (
              <Suspense fallback={suspenseFallback}>
                <Outlet />
              </Suspense>
            ),
            children: [
              { path: '/', element: <Navigate to="/dashboard" replace /> },
              { path: '/dashboard', element: <AssistantHomePage /> },
              { path: '/overview', element: <DashboardPage /> },
              {
                path: '/students',
                element: <StudentsLayoutPage />,
                children: [
                  { index: true, element: <StudentsPage /> },
                  { path: 'add-existing', element: <AddExistingStudentPage /> },
                  { path: 'duplicates', element: <DuplicateStudentsPage /> },
                ],
              },
              { path: '/students/:phone', element: <StudentProfilePage /> },
              { path: '/students/:phone/edit', element: <EditStudentPage /> },
              { path: '/enroll', element: <EnrollPage /> },
              { path: '/edit-student', element: <EditStudentPage /> },
              { path: '/extend', element: <LegacyEditRedirect tab="enrollment" /> },
              { path: '/override', element: <LegacyEditRedirect tab="corrections" /> },
              {
                path: '/placements',
                element: <PlacementsLayoutPage />,
                children: [
                  { index: true, element: <Navigate to="jobs" replace /> },
                  { path: 'jobs', element: <JobsPage /> },
                  { path: 'jobs/:jobId', element: <JobDetailPage /> },
                  { path: 'candidates', element: <CandidatesPage /> },
                  { path: 'students/:phone', element: <PlacementsStudentProfilePage /> },
                  { path: 'applications', element: <ApplicationsPage /> },
                  { path: 'tracker', element: <TrackerPage /> },
                  { path: 'companies', element: <CompaniesPage /> },
                  { path: 'recruiters', element: <RecruitersPage /> },
                  { path: 'community-links', element: <PlacementsCommunityLinksPage /> },
                  { path: 'history', element: <PlacementHistoryPage /> },
                  { path: 'inbox', element: <PlacementsInboxPage /> },
                  { path: 'add-existing-student', element: <PlacementsAddExistingStudentPage /> },
                ],
              },
              {
                path: '/support',
                element: <SupportLayoutPage />,
                children: [
                  { index: true, element: <Navigate to="community-links" replace /> },
                  { path: 'community-links', element: <CommunityLinksPage /> },
                  { path: 'outreach', element: <OutreachFollowupsPage /> },
                  {
                    path: 'chatbot',
                    element: <ChatbotLayoutPage />,
                    children: [
                      { index: true, element: <ChatbotInboxPage /> },
                      { path: 'kb', element: <ChatbotKbPage /> },
                      { path: 'missed', element: <ChatbotMissedPage /> },
                      { path: 'analytics', element: <ChatbotAnalyticsPage /> },
                      { path: ':ticketId', element: <ChatbotTicketDetailPage /> },
                    ],
                  },
                ],
              },
              {
                path: '/analytics',
                element: <AnalyticsLayoutPage />,
                children: [
                  { index: true, element: <AnalyticsOverviewPage /> },
                  { path: 'placements', element: <PlacementsAnalyticsPage /> },
                ],
              },
              { path: '/settings', element: <SettingsPage /> },
              { path: '/settings/website-sync', element: <WebsiteSyncSettingsPage /> },
              { path: '/settings/course-attempts', element: <CourseAttemptsSettingsPage /> },
              { path: '/admin/website-sync', element: <WebsiteSyncConsolePage /> },
              { path: '/activity', element: <ActivityPage /> },
              { path: '/notifications', element: <NotificationsPage /> },
              {
                path: '/docs',
                element: (
                  <Suspense fallback={suspenseFallback}>
                    <DocsPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
])
